/**
 * Data transformation for expanded row table
 */
import type { TFunction } from 'i18next'
import type { ExpandedRowMetadata } from './types'
import { getTemplateFields, isAddonActiveForSummary } from './constants'
import { calculateFieldValue, calculateRowPrice } from './utils'

/**
 * Transform service row data for expanded row display
 */
export const createExpandedRowData = (row: any, _t: TFunction) => {
    const serviceTemplate = row?.service_template
    const fields = getTemplateFields(serviceTemplate)

    const mainConfigs = (row.configoptions || []).map((config: any) => ({
        ...config,
        _isAddon: false,
    }))

    const addonGroups = (row.addons || []).map((addon: any) => ({
        addon_service_id: addon.addon_service_id,
        addon_service_name: addon.addon_service_name,
        addon_service_status: addon.addon_service_status,
        addon_pricing_model: addon.addon_pricing_model,
        addon_price_recurring: addon.addon_price_recurring,
        configs: (addon.configoptions || []).map((config: any) => ({
            ...config,
            _isAddon: true,
        })),
    }))

    const result: any[] = []

    const createRowWithFields = (
        configs: any[],
        metadata: ExpandedRowMetadata
    ) => {
        const rowData: any = {
            _id: metadata._id,
            _isAddon: metadata._isAddon,
            _isSummary: metadata._isSummary,
            service_id: metadata.service_id,
            service_name: metadata.service_name,
            addon_service_id: metadata.addon_service_id,
            addon_service_name: metadata.addon_service_name,
            _pricingModel: metadata._pricingModel,
            _priceRecurring: metadata._priceRecurring,
        }

        fields.forEach((field) => {
            const fieldValue = calculateFieldValue(
                configs,
                field,
                serviceTemplate
            )
            rowData[`_field_${field.key}`] = fieldValue
        })

        return rowData
    }

    // Main service row
    const mainPricingModel = row?.service_pricing_model
    const mainPriceRecurring = calculateRowPrice(
        mainPricingModel,
        row?.configoptions,
        row?.service_price_recurring
    )

    if (mainConfigs.length > 0 || row?.service_id) {
        result.push(
            createRowWithFields(mainConfigs, {
                _id: `main-${row.service_id}`,
                _isAddon: false,
                _isSummary: false,
                service_id: row.service_id,
                service_name: row.service_name,
                _pricingModel: mainPricingModel,
                _priceRecurring: mainPriceRecurring,
            })
        )
    }

    // Addon rows
    let totalRecurring = mainPriceRecurring
    addonGroups.forEach((addon: any) => {
        const addonPrice = calculateRowPrice(
            addon.addon_pricing_model,
            addon.configs,
            addon.addon_price_recurring
        )
        // Only add to total if addon is active (not excluded status)
        if (isAddonActiveForSummary(addon)) {
            totalRecurring += addonPrice
        }

        if (addon.configs.length > 0) {
            result.push(
                createRowWithFields(addon.configs, {
                    _id: `addon-${addon.addon_service_id}`,
                    _isAddon: true,
                    _isSummary: false,
                    addon_service_id: addon.addon_service_id,
                    addon_service_name: addon.addon_service_name,
                    _pricingModel: addon.addon_pricing_model,
                    _priceRecurring: addonPrice,
                })
            )
        }
    })

    // Summary row (only if has addons with configs)
    // Exclude addons with status: pending-terminated, rejected, cancelled
    const activeAddons = addonGroups.filter(isAddonActiveForSummary)
    if (activeAddons.some((a: any) => a.configs.length > 0)) {
        const allConfigs = [
            ...mainConfigs,
            ...activeAddons.flatMap((a: any) => a.configs),
        ]

        result.push(
            createRowWithFields(allConfigs, {
                _id: `summary-${row.service_id}`,
                _isAddon: false,
                _isSummary: true,
                _priceRecurring: totalRecurring,
            })
        )
    }

    return result
}
